// OMML (формулы Word) и MathML -> LaTeX; формулы в HTML из буфера Word (<!--[if gte msEquation 12]>…<![endif]-->).
// Разбор терпимый: HTML-обёртки Word внутри OMML (<span>, <i>) и незакрытые теги не мешают.
import { GREEK_BY_CHAR, SYM_BY_CHAR, FUNC_NAMES, MACRO_FUNCS } from './latex';
import { normalizeMathAlnum } from './unicodemath';

// ------------------------------------------------------------------------------------------------ общее
const NARY: Record<string, string> = { '∑': 'sum', '∏': 'prod', '∐': 'coprod', '∫': 'int', '∬': 'iint', '∭': 'iiint',
  '∮': 'oint', '∯': 'oint', '⋃': 'bigcup', '⋂': 'bigcap', '⋁': 'bigvee', '⋀': 'bigwedge' };
const ACC: Record<string, string> = { '̂': 'hat', '^': 'hat', 'ˆ': 'hat', '̃': 'tilde', '~': 'tilde', '˜': 'tilde',
  '̄': 'bar', '̅': 'bar', '¯': 'bar', '‾': 'bar', '̇': 'dot', '˙': 'dot', '̈': 'ddot', '¨': 'ddot',
  '⃗': 'vec', '→': 'vec', '⃖': 'overleftarrow', '̌': 'check', 'ˇ': 'check', '̆': 'breve', '˘': 'breve',
  '́': 'acute', '̀': 'grave', '⃡': 'overleftrightarrow', '̊': 'mathring' };
const DELIM: Record<string, string> = { '(': '(', ')': ')', '[': '[', ']': ']', '{': '\\{', '}': '\\}', '|': '|', '‖': '\\|',
  '⟨': '\\langle ', '⟩': '\\rangle ', '〈': '\\langle ', '〉': '\\rangle ', '⌈': '\\lceil ', '⌉': '\\rceil ', '⌊': '\\lfloor ',
  '⌋': '\\rfloor ', '': '.' };

/** Символы текста формулы -> LaTeX (греческие, знаки, минус). */
export function charsToTex(t: string, upright = false): string {
  let out = '';
  for (const ch of normalizeMathAlnum(t)) {
    if (ch in GREEK_BY_CHAR) out += '\\' + GREEK_BY_CHAR[ch] + ' ';
    else if (ch === '−' || ch === '–') out += '-';
    else if (ch === '⁡' || ch === '⁢' || ch === '⁣' || ch === '​') continue;
    else if (ch === ' ') out += '\\ ';
    else if (ch === ' ' || ch === ' ' || ch === ' ') out += ch === ' ' ? '\\,' : ch === ' ' ? '\\;' : '\\quad ';
    else if (ch === '{' || ch === '}' || ch === '%' || ch === '#' || ch === '_' || ch === '$') out += '\\' + ch;
    else if (ch === '\\') out += '\\backslash ';
    else if (ch === '°') out += '^{\\circ}';
    else if (ch === '⋯') out += '\\ldots ';
    else if (ch === '·' || ch === '⋅' || ch === '∙') out += '\\cdot ';
    else if (ch in SYM_BY_CHAR && !/[<>=+\-,.;:!?|'/()[\]]/.test(ch)) out += '\\' + SYM_BY_CHAR[ch] + ' ';
    else if (ch === '′') out += "'";
    else out += ch;
  }
  if (upright && /^[A-Za-z]{2,}$/.test(out.trim()) && !FUNC_NAMES.includes(out.trim())) return '\\mathrm{' + out.trim() + '}';
  if (upright && /^[A-Za-z]$/.test(out.trim())) return '\\mathrm{' + out.trim() + '}';
  return out;
}

/** Самозакрывающиеся теги XML -> пара тегов (иначе HTML-разбор вкладывает соседей внутрь). */
function expandSelfClosing(xml: string): string {
  return xml.replace(/<([A-Za-z][\w.-]*:[\w.-]+|[A-Za-z][\w.-]*)(\s[^<>]*?)?\/>/g, (_m, tag, attrs) => `<${tag}${attrs || ''}></${tag}>`);
}

function htmlDoc(markup: string): Document {
  return new DOMParser().parseFromString('<!doctype html><html><body>' + markup + '</body></html>', 'text/html');
}

const loc = (el: Element) => el.localName.toLowerCase().replace(/^(m|mml|w):/, '');
const pref = (el: Element) => { const m = el.localName.toLowerCase().match(/^([a-z]+):/); return m ? m[1] : ''; };
const kids = (el: Element) => Array.from(el.children);
function child(el: Element, name: string): Element | null {
  for (const k of kids(el)) if (pref(k) === 'm' && loc(k) === name) return k;
  return null;
}
function attr(el: Element | null, name: string): string | null {
  if (!el) return null;
  return el.getAttribute('m:' + name) ?? el.getAttribute(name);
}
function prop(el: Element, pr: string, name: string): string | null {
  const p = child(el, pr);
  if (!p) return null;
  const e = child(p, name);
  if (!e) return null;
  return attr(e, 'val') ?? '';
}

// ------------------------------------------------------------------------------------------------ OMML -> LaTeX
class Omml {
  /** Последовательность детей (m:r, конструкции, обёртки HTML). */
  seq(el: Element | null): string {
    if (!el) return '';
    let out = '';
    for (const k of kids(el)) out += this.node(k);
    return out;
  }

  run(r: Element): string {
    // текст: m:t, иначе текст вложенных HTML-обёрток Word
    let text = '';
    let brk = false;
    const collect = (e: Element) => {
      for (const n of Array.from(e.childNodes)) {
        if (n.nodeType === 3) { text += n.textContent || ''; continue; }
        if (n.nodeType !== 1) continue;
        const x = n as Element;
        const nm = loc(x);
        if (pref(x) === 'm' && nm === 'rpr') { if (child(x, 'brk')) brk = true; continue; }
        if (pref(x) === 'w' && (nm === 'rpr')) continue;
        if (pref(x) === 'w' && nm === 'br') { brk = true; continue; }
        collect(x);
      }
    };
    collect(r);
    const rpr = child(r, 'rpr');
    const nor = !!(rpr && child(rpr, 'nor'));
    const sty = rpr ? prop(r, 'rpr', 'sty') : null;
    let tex: string;
    if (nor && /\S/.test(text) && !/^[\d\s.,+\-−=()]+$/.test(text)) tex = '\\text{' + text.replace(/[{}\\]/g, '') + '}';
    else if (sty === 'p' || sty === 'b') {
      const t = charsToTex(text);
      if (/^[A-Za-z]+$/.test(t.trim())) {
        const w = t.trim();
        tex = FUNC_NAMES.includes(w) ? '\\' + w + ' ' : (sty === 'b' ? '\\mathbf{' + w + '}' : '\\mathrm{' + w + '}');
      } else tex = t;
    } else tex = charsToTex(text);
    return (brk ? ' \\\\ ' : '') + tex;
  }

  node(el: Element): string {
    const p = pref(el);
    const n = loc(el);
    if (p === 'w' || n.endsWith('pr')) return '';             // свойства
    if (p !== 'm') return this.seq(el) + (kids(el).length ? '' : (el.textContent && el.localName.toLowerCase() !== 'script' ? charsToTex(el.textContent) : ''));
    switch (n) {
      case 'omathpara': return kids(el).filter((k) => loc(k) === 'omath').map((k) => this.seq(k)).join(' \\\\ ');
      case 'omath': case 'e': case 'num': case 'den': case 'sub': case 'sup': case 'deg': case 'lim': case 'fname': case 'box':
      case 'phant':
        return this.seq(el);
      case 'r': return this.run(el);
      case 't': return charsToTex(el.textContent || '');
      case 'f': {
        const type = prop(el, 'fpr', 'type');
        const a = this.seq(child(el, 'num'));
        const b = this.seq(child(el, 'den'));
        if (type === 'lin') return grp(a) + '/' + grp(b);
        if (type === 'nobar') return '\\binom{' + a + '}{' + b + '}';
        return '\\frac{' + a + '}{' + b + '}';
      }
      case 'ssup': return base(this.seq(child(el, 'e'))) + '^{' + this.seq(child(el, 'sup')) + '}';
      case 'ssub': return base(this.seq(child(el, 'e'))) + '_{' + this.seq(child(el, 'sub')) + '}';
      case 'ssubsup': return base(this.seq(child(el, 'e'))) + '_{' + this.seq(child(el, 'sub')) + '}^{' + this.seq(child(el, 'sup')) + '}';
      case 'spre': return '{}_{' + this.seq(child(el, 'sub')) + '}^{' + this.seq(child(el, 'sup')) + '}' + this.seq(child(el, 'e'));
      case 'rad': {
        const deg = this.seq(child(el, 'deg')).trim();
        const hide = prop(el, 'radpr', 'deghide');
        const e = this.seq(child(el, 'e'));
        return deg && hide !== '1' && hide !== 'on' && hide !== 'true' ? '\\sqrt[' + deg + ']{' + e + '}' : '\\sqrt{' + e + '}';
      }
      case 'd': {
        const beg = prop(el, 'dpr', 'begchr') ?? '(';
        const end = prop(el, 'dpr', 'endchr') ?? ')';
        const sep = prop(el, 'dpr', 'sepchr') ?? '|';
        const es = kids(el).filter((k) => pref(k) === 'm' && loc(k) === 'e');
        // матрица в скобках -> pmatrix/bmatrix/vmatrix
        if (es.length === 1) {
          const only = kids(es[0]).filter((k) => pref(k) === 'm');
          if (only.length === 1 && loc(only[0]) === 'm') {
            const env = beg === '(' ? 'pmatrix' : beg === '[' ? 'bmatrix' : beg === '|' ? 'vmatrix' : beg === '‖' ? 'Vmatrix' : beg === '{' ? 'Bmatrix' : '';
            if (env && ((end === ')' && beg === '(') || (end === ']' && beg === '[') || (end === '|' && beg === '|') || (end === '‖' && beg === '‖') || (end === '}' && beg === '{'))) {
              return this.matrix(only[0], env);
            }
          }
          if (only.length === 1 && loc(only[0]) === 'eqarr' && beg === '{' && (end === '' || end === '.')) {
            return this.eqarr(only[0], 'cases');
          }
        }
        const inner = es.map((e) => this.seq(e)).join(sep === '|' ? '\\mid ' : charsToTex(sep));
        const L = DELIM[beg] ?? charsToTex(beg);
        const R = DELIM[end] ?? charsToTex(end);
        if (beg === '(' && end === ')' && !/\\(frac|sum|int|prod|sqrt|begin|binom)/.test(inner)) return '(' + inner + ')';
        return '\\left' + L + inner + '\\right' + R;
      }
      case 'nary': {
        const ch = prop(el, 'narypr', 'chr') ?? '∫';
        const name = NARY[ch];
        const subHide = prop(el, 'narypr', 'subhide');
        const supHide = prop(el, 'narypr', 'suphide');
        const sub = subHide === '1' || subHide === 'on' ? '' : this.seq(child(el, 'sub')).trim();
        const sup = supHide === '1' || supHide === 'on' ? '' : this.seq(child(el, 'sup')).trim();
        let s = name ? '\\' + name : charsToTex(ch);
        if (sub) s += '_{' + sub + '}';
        if (sup) s += '^{' + sup + '}';
        return s + ' ' + this.seq(child(el, 'e'));
      }
      case 'func': {
        const fn = child(el, 'fname');
        let name = this.seq(fn).trim();
        const bare = name.replace(/^\\mathrm\{([^}]*)\}$/, '$1').replace(/^\\([A-Za-z]+)\s*$/, '$1');
        if (/^[A-Za-z]+$/.test(bare)) {
          name = FUNC_NAMES.includes(bare) || MACRO_FUNCS.includes(bare) ? '\\' + bare + ' ' : '\\operatorname{' + bare + '}';
        }
        return name + ' ' + this.seq(child(el, 'e'));
      }
      case 'limlow': {
        const e = this.seq(child(el, 'e')).trim();
        const l = this.seq(child(el, 'lim'));
        const w = e.replace(/^\\mathrm\{([^}]*)\}$/, '$1').replace(/^\\/, '').trim();
        if (/^(lim|max|min|sup|inf|limsup|liminf)$/.test(w)) return '\\' + w + '_{' + l + '}';
        if (e === '\\to ' || e === '\\to' || e === '→' || e === '\\rightarrow') return '\\xrightarrow[' + l + ']{}';
        return '\\underset{' + l + '}{' + e + '}';
      }
      case 'limupp': {
        const e = this.seq(child(el, 'e')).trim();
        const l = this.seq(child(el, 'lim'));
        if (/^\\(to|rightarrow)\s*$/.test(e) || e === '→') return '\\xrightarrow{' + l + '}';
        return '\\overset{' + l + '}{' + e + '}';
      }
      case 'acc': {
        const ch = prop(el, 'accpr', 'chr') ?? '̂';
        const cmd = ACC[ch] || 'hat';
        return '\\' + cmd + '{' + this.seq(child(el, 'e')) + '}';
      }
      case 'bar': {
        const pos = prop(el, 'barpr', 'pos') ?? 'bot';
        return (pos === 'top' ? '\\overline{' : '\\underline{') + this.seq(child(el, 'e')) + '}';
      }
      case 'groupchr': {
        const ch = prop(el, 'groupchrpr', 'chr') ?? '⏟';
        const pos = prop(el, 'groupchrpr', 'pos') ?? 'bot';
        const e = this.seq(child(el, 'e'));
        if (ch === '⏟' || ch === '︸') return '\\underbrace{' + e + '}';
        if (ch === '⏞' || ch === '︷') return '\\overbrace{' + e + '}';
        return (pos === 'top' ? '\\overset{' : '\\underset{') + charsToTex(ch) + '}{' + e + '}';
      }
      case 'borderbox': return '\\boxed{' + this.seq(child(el, 'e')) + '}';
      case 'm': return this.matrix(el, 'matrix');
      case 'eqarr': return this.eqarr(el, 'aligned');
      default: return this.seq(el);
    }
  }

  matrix(el: Element, env: string): string {
    const rows = kids(el).filter((k) => pref(k) === 'm' && loc(k) === 'mr')
      .map((mr) => kids(mr).filter((k) => pref(k) === 'm' && loc(k) === 'e').map((e) => this.seq(e).trim()).join(' & '));
    return '\\begin{' + env + '}' + rows.join(' \\\\ ') + '\\end{' + env + '}';
  }

  eqarr(el: Element, env: string): string {
    const rows = kids(el).filter((k) => pref(k) === 'm' && loc(k) === 'e').map((e) => this.seq(e).trim().replace(/\\&/g, '&'));
    return '\\begin{' + env + '}' + rows.join(' \\\\ ') + '\\end{' + env + '}';
  }
}

function grp(s: string) { const t = s.trim(); return t.length <= 1 || /^[A-Za-z0-9]+$/.test(t) ? t : '(' + t + ')'; }
function base(s: string) { const t = s.trim(); return /^(\\[A-Za-z]+\s*|[A-Za-z0-9])$/.test(t) || /^\\[A-Za-z]+\{[^{}]*\}$/.test(t) ? t : '{' + t + '}'; }

export interface OmmlResult { latex: string; display: boolean; numbered: boolean; num: string | null }

/** OMML-разметка (m:oMath / m:oMathPara, с HTML-обёртками Word или без) -> LaTeX. */
export function ommlToLatex(omml: string): OmmlResult {
  const src = expandSelfClosing(String(omml || '').replace(/<\?xml[^>]*>/, '').replace(/&nbsp;/g, ' '));
  const doc = htmlDoc(src);
  const conv = new Omml();
  const body = doc.body;
  const para = Array.from(body.getElementsByTagName('*')).find((e) => e.localName.toLowerCase() === 'm:omathpara');
  let latex = '';
  const roots = kids(body).length ? kids(body) : [];
  for (const r of roots) latex += conv.node(r);
  latex = latex.replace(/\s+/g, ' ').trim();
  // одна строка в m:eqArr (так Word хранит формулу с номером «#(1)») - без окружения
  latex = latex.replace(/^\\begin\{aligned\}([\s\S]*)\\end\{aligned\}$/, (all, inner) => (/\\\\/.test(inner) ? all : inner.trim()));
  // номер Word «…#(1)» (формула с номером через #)
  let num: string | null = null;
  const m = latex.match(/^(.*?)\s*\\#\s*\(\s*([А-ЯA-Z]?\.?\d+(?:\.\d+)?)\s*\)\s*$/);
  if (m) { latex = m[1].trim(); num = m[2]; }
  return { latex: tidy(latex), display: !!para, numbered: !!num, num };
}

/** Мелкая уборка результата. */
function tidy(s: string): string {
  return s.replace(/\s+/g, ' ').replace(/\s*([_^])\s*/g, '$1').replace(/\{\s+/g, '{').replace(/\s+\}/g, '}')
    .replace(/\\([A-Za-z]+) (?=[^A-Za-z\s])/g, '\\$1').replace(/\} (?=[A-Za-z\\(])/g, '}').trim();
}

// ------------------------------------------------------------------------------------------------ MathML -> LaTeX
const MO: Record<string, string> = { '×': '\\times ', '·': '\\cdot ', '⋅': '\\cdot ', '∙': '\\cdot ', '−': '-', '-': '-',
  '≤': '\\le ', '≥': '\\ge ', '≠': '\\ne ', '≈': '\\approx ', '∞': '\\infty ', '→': '\\to ', '±': '\\pm ', '∓': '\\mp ',
  '∂': '\\partial ', '∈': '\\in ', '∉': '\\notin ', '…': '\\ldots ', '⋯': '\\ldots ', '⁡': '', '⁢': '', '⁣': ' ',
  '{': '\\{', '}': '\\}', '∇': '\\nabla ', '∀': '\\forall ', '∃': '\\exists ', '⇒': '\\Rightarrow ', '⇔': '\\Leftrightarrow ',
  '∧': '\\land ', '∨': '\\lor ', '¬': '\\neg ', '∪': '\\cup ', '∩': '\\cap ', '⊂': '\\subset ', '⊆': '\\subseteq ',
  '∅': '\\emptyset ', '°': '^{\\circ}', '%': '\\%', '#': '\\#', '&': '\\&', '′': "'", '″': "''" };
const FENCE = new Set(['(', ')', '[', ']', '{', '}', '|', '‖', '⟨', '⟩', '〈', '〉', '⌈', '⌉', '⌊', '⌋']);

class MathML {
  kids(el: Element) { return Array.from(el.children); }
  conv(el: Element): string {
    const name = el.localName.toLowerCase().replace(/^(mml|m):/, '');
    const ks = this.kids(el);
    const text = (el.textContent || '').trim();
    switch (name) {
      case 'math': case 'mrow': case 'mstyle': case 'mpadded': case 'merror': case 'mtd': case 'mlabeledtr':
        return this.row(ks);
      case 'semantics': {
        const tex = ks.find((k) => k.localName.toLowerCase() === 'annotation' && /tex/i.test(k.getAttribute('encoding') || ''));
        if (tex && tex.textContent) return tex.textContent.trim();
        return ks[0] ? this.conv(ks[0]) : '';
      }
      case 'annotation': case 'annotation-xml': case 'mphantom': case 'none': case 'mprescripts': return '';
      case 'mi': {
        const t = normalizeMathAlnum(text);
        if (FUNC_NAMES.includes(t)) return '\\' + t + ' ';
        if (t.length > 1 && /^[A-Za-z]+$/.test(t)) return '\\mathrm{' + t + '}';
        if (el.getAttribute('mathvariant') === 'normal' && /^[A-Za-z]$/.test(t)) return '\\mathrm{' + t + '}';
        if (el.getAttribute('mathvariant') === 'double-struck') return '\\mathbb{' + t + '}';
        if (/bold/.test(el.getAttribute('mathvariant') || '')) return '\\mathbf{' + charsToTex(t) + '}';
        return charsToTex(t);
      }
      case 'mn': return normalizeMathAlnum(text).replace(/(\d)[.,](\d)/g, '$1{,}$2');
      case 'mo': {
        const t = normalizeMathAlnum(text);
        if (t in MO) return MO[t];
        if (t in NARY) return '\\' + NARY[t] + ' ';
        return charsToTex(t);
      }
      case 'mtext': return text ? '\\text{' + text.replace(/[{}\\]/g, '') + '}' : '\\ ';
      case 'ms': return '\\text{"' + text + '"}';
      case 'mspace': return '\\,';
      case 'msup': return base(this.conv(ks[0])) + '^{' + this.conv(ks[1]) + '}';
      case 'msub': return base(this.conv(ks[0])) + '_{' + this.conv(ks[1]) + '}';
      case 'msubsup': return base(this.conv(ks[0])) + '_{' + this.conv(ks[1]) + '}^{' + this.conv(ks[2]) + '}';
      case 'mfrac': {
        const lt = el.getAttribute('linethickness');
        if (lt === '0' || lt === '0px') return '\\binom{' + this.conv(ks[0]) + '}{' + this.conv(ks[1]) + '}';
        if (el.getAttribute('bevelled') === 'true') return grp(this.conv(ks[0])) + '/' + grp(this.conv(ks[1]));
        return '\\frac{' + this.conv(ks[0]) + '}{' + this.conv(ks[1]) + '}';
      }
      case 'msqrt': return '\\sqrt{' + this.row(ks) + '}';
      case 'mroot': return '\\sqrt[' + this.conv(ks[1]) + ']{' + this.conv(ks[0]) + '}';
      case 'mover': case 'munder': case 'munderover': {
        const b = this.conv(ks[0]).trim();
        const bt = normalizeMathAlnum((ks[0].textContent || '').trim());
        const isNary = bt in NARY || /^\\(sum|prod|int|lim|max|min|bigcup|bigcap)/.test(b);
        if (name === 'mover') {
          const acc = (ks[1]?.textContent || '').trim();
          if (acc in ACC && el.getAttribute('accent') !== 'false') return '\\' + ACC[acc] + '{' + b + '}';
          if (acc === '⏞' || acc === '︷') return '\\overbrace{' + b + '}';
          if (isNary) return b + '^{' + this.conv(ks[1]) + '}';
          return '\\overset{' + this.conv(ks[1]) + '}{' + b + '}';
        }
        if (name === 'munder') {
          const u = (ks[1]?.textContent || '').trim();
          if (u === '⏟' || u === '︸') return '\\underbrace{' + b + '}';
          if (u === '_' || u === '‾' || u === '¯') return '\\underline{' + b + '}';
          if (isNary) return b + '_{' + this.conv(ks[1]) + '}';
          return '\\underset{' + this.conv(ks[1]) + '}{' + b + '}';
        }
        if (isNary) return b + '_{' + this.conv(ks[1]) + '}^{' + this.conv(ks[2]) + '}';
        return '\\overset{' + this.conv(ks[2]) + '}{\\underset{' + this.conv(ks[1]) + '}{' + b + '}}';
      }
      case 'mfenced': {
        const o = el.getAttribute('open') ?? '(';
        const cl = el.getAttribute('close') ?? ')';
        const sep = el.getAttribute('separators') ?? ',';
        if (ks.length === 1 && ks[0].localName.toLowerCase() === 'mtable') return this.table(ks[0], o, cl);
        return '\\left' + (DELIM[o] ?? o) + ks.map((k) => this.conv(k)).join(sep.trim()[0] || ',') + '\\right' + (DELIM[cl] ?? cl);
      }
      case 'menclose': return /box|roundedbox/.test(el.getAttribute('notation') || '') ? '\\boxed{' + this.row(ks) + '}' : this.row(ks);
      case 'mtable': return this.table(el, '', '');
      case 'mtr': return this.kids(el).map((k) => this.conv(k)).join(' & ');
      case 'mmultiscripts': {
        const b = ks[0] ? this.conv(ks[0]) : '';
        let s = base(b);
        for (let i = 1; i + 1 < ks.length; i += 2) {
          if (ks[i].localName.toLowerCase() === 'mprescripts') break;
          const sb = this.conv(ks[i]); const sp = this.conv(ks[i + 1]);
          if (sb) s += '_{' + sb + '}';
          if (sp) s += '^{' + sp + '}';
        }
        return s;
      }
      default: return this.row(ks) || charsToTex(text);
    }
  }

  /** Строка: скобки-«растяжки» mo вокруг таблицы -> матрица/система. */
  row(ks: Element[]): string {
    const out: string[] = [];
    for (let i = 0; i < ks.length; i++) {
      const k = ks[i];
      const nm = k.localName.toLowerCase().replace(/^(mml|m):/, '');
      const t = (k.textContent || '').trim();
      if (nm === 'mo' && FENCE.has(t)) {
        // ( mtable ) -> pmatrix; { mtable -> cases
        const nx = ks[i + 1];
        if (nx && /mtable$/.test(nx.localName.toLowerCase())) {
          const after = ks[i + 2];
          const close = after && /mo$/.test(after.localName.toLowerCase()) && FENCE.has((after.textContent || '').trim()) ? (after.textContent || '').trim() : '';
          out.push(this.table(nx, t, close));
          i += close ? 2 : 1;
          continue;
        }
        if (k.getAttribute('stretchy') === 'true' || k.getAttribute('fence') === 'true') {
          out.push((/[([{⟨|‖⌈⌊]/.test(t) && !/[)\]}⟩]/.test(t) ? '\\left' : '\\right') + (DELIM[t] ?? t));
          continue;
        }
        out.push(t === '{' ? '\\{' : t === '}' ? '\\}' : t === '‖' ? '\\|' : t);
        continue;
      }
      out.push(this.conv(k));
    }
    let s = out.join('');
    // непарные \left/\right - убрать
    const l = (s.match(/\\left/g) || []).length;
    const r = (s.match(/\\right/g) || []).length;
    if (l !== r) s = s.replace(/\\left|\\right/g, '');
    return s;
  }

  table(el: Element, open: string, close: string): string {
    const rows = this.kids(el).map((tr) => this.kids(tr).map((td) => this.conv(td).trim()).join(' & '));
    const body = rows.join(' \\\\ ');
    const env = open === '(' ? 'pmatrix' : open === '[' ? 'bmatrix' : open === '|' ? 'vmatrix' : open === '‖' ? 'Vmatrix'
      : open === '{' && !close ? 'cases' : open === '{' ? 'Bmatrix' : 'matrix';
    return '\\begin{' + env + '}' + body + '\\end{' + env + '}';
  }
}

/** MathML (строка) -> LaTeX. */
export function mathmlToLatex(mathml: string): { latex: string; display: boolean } {
  const src = String(mathml || '').replace(/<(\/?)(mml|m):/g, '<$1');
  const doc = htmlDoc(src);
  const root = doc.body.querySelector('math') || doc.body;
  const latex = tidy(new MathML().conv(root));
  return { latex, display: (root.getAttribute?.('display') || '') === 'block' || root.getAttribute?.('mode') === 'display' };
}

// ------------------------------------------------------------------------------------------------ HTML из Word
const EQ_RE = /<!--\[if gte msEquation 12\]>([\s\S]*?)<!\[endif\]-->(\s*<!\[if !msEquation\]>[\s\S]*?<!\[endif\]>)?/gi;

export function hasWordEquations(html: string): boolean { return /msEquation|<m:oMath/i.test(html || ''); }

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * HTML из буфера Word: формулы (OMML в условных комментариях) -> узлы редактора:
 *   строчные - <span data-type="math-inline" data-latex>, выключные (абзац только с формулой, в т. ч. с номером
 *   «(1)» через табуляцию или в таблице-раскладке «формула | (N)») - <div data-type="formula">;
 *   абзацы «где x – …;» сразу после формулы - в экспликацию формулы (data-where).
 * Картинки-заменители формул (<![if !msEquation]>) удаляются. Возвращает новый HTML и число формул.
 */
export function convertWordEquations(html: string, fix: (latex: string) => string = (x) => x): { html: string; count: number } {
  let count = 0;
  let out = String(html || '').replace(EQ_RE, (_m, omml: string) => {
    const r = ommlToLatex(omml);
    if (!r.latex) return '';
    count++;
    const latex = fix(r.latex);
    const tag = r.display ? 'data-sto-display="1"' : '';
    return `<span data-type="math-inline" data-latex="${esc(latex)}" ${tag} data-num="${esc(r.num || '')}">${esc(latex)}</span>`;
  });
  // формулы без условных комментариев (OMML прямо в HTML)
  out = out.replace(/<m:oMathPara[\s\S]*?<\/m:oMathPara>|<m:oMath[\s>][\s\S]*?<\/m:oMath>/gi, (m) => {
    const r = ommlToLatex(m);
    if (!r.latex) return '';
    count++;
    const latex = fix(r.latex);
    return `<span data-type="math-inline" data-latex="${esc(latex)}" ${r.display ? 'data-sto-display="1"' : ''}>${esc(latex)}</span>`;
  });
  if (!count) return { html, count: 0 };
  return { html: promoteDisplay(out), count };
}

const NUM_RE = /^\s*\(\s*([А-ЯA-Z]\.)?\d+(?:\.\d+)?\s*\)\s*[.,;]?\s*$/;

/** Абзацы и таблицы-раскладки, где кроме формулы ничего нет (или только номер) -> выключные формулы. */
function promoteDisplay(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const spans = Array.from(doc.querySelectorAll('span[data-type="math-inline"]'));
  const made: Element[] = [];
  for (const sp of spans) {
    // таблица-раскладка: [формула | (N)]
    const cell = sp.closest('td,th');
    const table = cell?.closest('table');
    if (table && cell) {
      const cells = Array.from(table.querySelectorAll('td,th'));
      const others = cells.filter((c) => c !== cell);
      const rows = table.querySelectorAll('tr').length;
      if (rows === 1 && cells.length <= 3 && others.every((c) => !(c.textContent || '').trim() || NUM_RE.test(c.textContent || ''))
        && onlyThis(cell, sp)) {
        const numbered = others.some((c) => NUM_RE.test(c.textContent || '')) || !!sp.getAttribute('data-num');
        const div = formulaDiv(doc, sp.getAttribute('data-latex') || '', numbered);
        table.replaceWith(div);
        made.push(div);
        continue;
      }
    }
    const block = sp.closest('p,div,li,h1,h2,h3,h4,h5,h6');
    if (!block || block.getAttribute('data-type') === 'formula') continue;
    const rest = textWithout(block, sp);
    const display = sp.getAttribute('data-sto-display') === '1';
    if (!rest.trim() || NUM_RE.test(rest)) {
      if (!display && !NUM_RE.test(rest) && block.querySelectorAll('span[data-type="math-inline"]').length > 1) continue;
      if (!display && !NUM_RE.test(rest) && !/\\(frac|sum|int|begin|sqrt|lim|prod)/.test(sp.getAttribute('data-latex') || '')) continue;
      const numbered = NUM_RE.test(rest) || !!sp.getAttribute('data-num') || display;
      const div = formulaDiv(doc, sp.getAttribute('data-latex') || '', numbered);
      block.replaceWith(div);
      made.push(div);
    }
  }
  // экспликация «где …» после выключной формулы
  for (const div of made) {
    const where: { sym: string; desc: string }[] = [];
    let p = div.nextElementSibling;
    let first = true;
    while (p && /^(P|DIV)$/.test(p.tagName) && p.getAttribute('data-type') !== 'formula') {
      const parsed = whereLine(p, first);
      if (!parsed) break;
      where.push(parsed.item);
      const nx = p.nextElementSibling;
      p.remove();
      p = nx;
      first = false;
      if (parsed.last) break;
    }
    if (where.length) {
      div.setAttribute('data-where', JSON.stringify(where));
      // запятая перед «где» ставится автоматически при экспорте - снять её из LaTeX
      const lt = (div.getAttribute('data-latex') || '').replace(/\s*,\s*$/, '');
      div.setAttribute('data-latex', lt);
    }
  }
  return doc.body.innerHTML;
}

function onlyThis(cell: Element, sp: Element): boolean { return !textWithout(cell, sp).trim(); }

function textWithout(block: Element, sp: Element): string {
  const clone = block.cloneNode(true) as Element;
  clone.querySelectorAll('span[data-type="math-inline"]').forEach((x) => { if (x.getAttribute('data-latex') === sp.getAttribute('data-latex')) x.remove(); });
  return (clone.textContent || '').replace(/[ \t]/g, ' ');
}

function formulaDiv(doc: Document, latex: string, numbered: boolean): Element {
  const div = doc.createElement('div');
  div.setAttribute('data-type', 'formula');
  div.setAttribute('data-latex', latex.replace(/\s*[,.;]\s*$/, (m) => m.trim()));
  div.setAttribute('data-numbered', numbered ? 'true' : 'false');
  return div;
}

/** Строка экспликации: «где m – масса образца, кг;» (символ - формула или текст до тире). */
function whereLine(p: Element, first: boolean): { item: { sym: string; desc: string }; last: boolean } | null {
  // текст абзаца: степени единиц (м<sup>3</sup>) -> «м³», формулы в расшифровке -> $…$
  const clone = p.cloneNode(true) as Element;
  const SUPC: Record<string, string> = { '2': '²', '3': '³', '-1': '⁻¹', '−1': '⁻¹', '-2': '⁻²' };
  clone.querySelectorAll('sup').forEach((s) => { const t = (s.textContent || '').trim(); s.replaceWith(SUPC[t] ?? t); });
  const spans = Array.from(clone.querySelectorAll('span[data-type="math-inline"]'));
  spans.forEach((s, i) => { if (i > 0) s.replaceWith('$' + (s.getAttribute('data-latex') || '') + '$'); });
  const text = (clone.textContent || '').replace(/[ \t]/g, ' ').replace(/\s+/g, ' ').trim();
  if (first && !/^где\s/i.test(text)) return null;
  const body = first ? text.replace(/^где\s*:?\s*/i, '') : text;
  const m = body.match(/^(.{1,40}?)\s+[–—-]\s+(.+)$/);
  if (!m) return null;
  const mathSpan = p.querySelector('span[data-type="math-inline"]');
  let sym = mathSpan ? mathSpan.getAttribute('data-latex') || m[1] : m[1];
  if (!mathSpan) sym = sym.trim();
  let desc = m[2].trim();
  const last = !/;\s*$/.test(desc);
  desc = desc.replace(/[;.]\s*$/, '').trim();
  return { item: { sym, desc }, last };
}
